import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import type { Database } from '@/integrations/supabase/types'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { AnalyticsCleanup } from '@/utils/analyticsCleanup'

type UserProfile = Database['public']['Tables']['users']['Row']

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, fullName: string) => Promise<void>
  signOut: (clearAllCache?: boolean) => Promise<void>
  updateProfile: (data: Partial<UserProfile>) => Promise<void>
  refreshUser: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  redirectAfterLogin: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  

  const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      console.log('Buscando perfil do usuário:', userId)
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('Erro na consulta do perfil:', error)
        throw error
      }
      
      if (data) {
        console.log('Perfil encontrado:', data)
        return data
      }

      console.warn('Perfil não encontrado, usando fallback para:', userId)
      // Tentar obter email do usuário autenticado atual
      let email = ''
      try {
        const { data: authData } = await supabase.auth.getUser()
        email = authData.user?.email || ''
      } catch {}
      return {
        id: userId,
        email,
        full_name: '',
        role: 'user' as const,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        phone: null
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
      // Retornar um perfil básico como fallback se não conseguir buscar
      return {
        id: userId,
        email: '',
        full_name: '',
        role: 'user' as const, // Manter 'user' para usuários novos
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        phone: null
      }
    }
  }

  const location = useLocation()
  const [hasRedirected, setHasRedirected] = useState(false)

  const redirectUserByRole = async (profile: UserProfile, force = false) => {
    // Evitar redirecionamentos múltiplos
    if (hasRedirected && !force) return;
    
    // Never redirect from login page unless forced (after successful sign in)
    // Only auto-redirect from root page for existing sessions
    // Don't redirect from reset-password page
    if (!force && location.pathname === '/login') return;
    if (!force && location.pathname === '/reset-password') return;
    if (!force && location.pathname !== '/') return;
    
    setHasRedirected(true)
    
    console.log('🔄 Redirecionando usuário por role:', profile.role);
    
    if (profile.role === 'super_admin') {
      console.log('🔄 Redirecionando super admin para /super-admin');
      navigate('/super-admin', { replace: true })
      return;
    }

    // Para company_admin e user, verificar se tem empresas usando consulta mais simples
    try {
      console.log('🔄 Buscando empresas do usuário:', profile.id);
      
      // Buscar vínculo do usuário com empresas (pega a mais antiga/principal)
      const { data: membership, error } = await supabase
        .from('user_companies')
        .select(`
          role,
          companies (
            slug
          )
        `)
        .eq('user_id', profile.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Erro na consulta de empresas:', error);
        throw error;
      }
      
      console.log('🔄 Resultado da consulta de empresas:', membership);
      
      const slug = membership?.companies?.slug || null;
      const companyRole = (membership as any)?.role as string | null;
      
      if (slug) {
        const isCompanyAdmin = ['owner','admin','manager'].includes(companyRole || '');
        const targetPath = isCompanyAdmin ? `/${slug}/admin` : `/${slug}`;
        console.log('🔄 Redirecionando para empresa:', slug, 'como', companyRole, '->', targetPath);
        navigate(targetPath, { replace: true });
      } else {
        // Usuário não tem empresa, redirecionar para onboarding
        console.log('🔄 Redirecionando para onboarding - sem empresas');
        navigate('/onboarding', { replace: true });
      }
    } catch (error) {
      console.error('Erro ao buscar empresas do usuário:', error)
      // Em caso de erro, redirecionar para onboarding para ser seguro
      console.log('🔄 Redirecionando para onboarding - erro na consulta');
      navigate('/onboarding', { replace: true })
    }
  }

  const redirectAfterLogin = async (retryCount = 0) => {
    try {
      if (!userProfile) {
        console.log('redirectAfterLogin: userProfile ausente, abortando')
        return
      }
      
      // Adicionar retry em caso de problemas de sincronização
      try {
        await redirectUserByRole(userProfile, true)
      } catch (error) {
        console.error('Erro no redirectUserByRole:', error)
        
        // Tentar novamente após delay se for o primeiro erro
        if (retryCount < 1) {
          console.log('Tentando novamente o redirecionamento em 2 segundos...')
          setTimeout(() => redirectAfterLogin(retryCount + 1), 2000)
          return
        }
        
        // Se falhar após retry, redirecionar para onboarding
        console.log('Redirecionamento falhou, indo para onboarding')
        navigate('/onboarding')
      }
    } catch (e) {
      console.error('redirectAfterLogin error:', e)
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        // Tratamento específico para diferentes tipos de erro
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Email ou senha incorretos. Verifique suas credenciais.');
        }
        if (error.message.includes('Email not confirmed')) {
          throw new Error('Por favor, confirme seu email antes de fazer login.');
        }
        throw error;
      }

      if (data.user) {
        const profile = await getUserProfile(data.user.id)
        if (profile) {
          setUserProfile(profile)
          console.log('🔄 [SignIn] Successful login, profile loaded:', profile.role);
          
          // Limpeza automática de analytics no login para dados consistentes
          setTimeout(() => {
            AnalyticsCleanup.performLoginCleanup(data.user.id);
          }, 0);
          
          toast.success(`Bem-vindo, ${profile.full_name || profile.email}!`)
          // Navigation will be handled by Login component useEffect
        }
      }
    } catch (error: any) {
      console.error('🔴 [AuthContext] Erro no signIn:', error);
      
      // Don't show toast here - let Login component handle the display
      // Just re-throw with proper error marking
      const authError = new Error(error.message);
      authError.name = 'AuthError';
      throw authError;
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      setLoading(true)
      console.log('Iniciando cadastro de usuário...', { email, fullName })
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
          }
        }
      })

      if (error) {
        console.error('Erro no signUp:', error)
        // Tratamento específico para diferentes tipos de erro
        if (error.message.includes('User already registered')) {
          throw new Error('Este email já está cadastrado. Tente fazer login ou use a opção "Esqueci minha senha".');
        }
        if (error.message.includes('Invalid email')) {
          throw new Error('Por favor, insira um email válido.');
        }
        if (error.message.includes('Password')) {
          throw new Error('A senha deve ter pelo menos 6 caracteres.');
        }
        throw error
      }

      console.log('SignUp bem-sucedido:', data)

      // Note: O perfil do usuário será criado automaticamente pelo trigger handle_new_user
      // que usa os dados de raw_user_meta_data
      
      toast.success("Conta criada com sucesso! Verifique seu email para confirmar a conta.")
      
    } catch (error: any) {
      console.error('Erro completo no signUp:', error)
      toast.error(error.message || "Erro desconhecido ao criar conta")
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async (clearAllCache: boolean = false) => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error

      setUser(null)
      setUserProfile(null)
      
      // Limpar todos os caches se solicitado
      if (clearAllCache) {
        // Limpar localStorage relacionado ao onboarding e empresa
        localStorage.removeItem('onboarding_step1_data')
        localStorage.removeItem('onboarding_step2_data')
        localStorage.removeItem('onboarding_current_step')
        
        // Limpar todos os dados de empresas do LocalStorage
        const keys = Object.keys(localStorage).filter(key => 
          key.startsWith('digimenu_') || 
          key.startsWith('company_') ||
          key.startsWith('alimenu_')
        )
        keys.forEach(key => localStorage.removeItem(key))
        
        // Limpar sessionStorage
        sessionStorage.clear()
        
        // Limpar cache de permissões e perfis
        // Estes serão limpos através dos hooks específicos quando necessário
      }
      
      navigate('/login', { replace: true })
      
      if (!clearAllCache) {
        toast.success("Você foi desconectado com sucesso.")
      }
    } catch (error: any) {
      toast.error(error.message)
    }
  }

  const updateProfile = async (data: Partial<UserProfile>) => {
    try {
      if (!user) throw new Error('Usuário não autenticado')

      const { error } = await supabase
        .from('users')
        .update(data)
        .eq('id', user.id)

      if (error) throw error

      setUserProfile(prev => prev ? { ...prev, ...data } : null)
      
      toast.success("Suas informações foram atualizadas com sucesso.")
    } catch (error: any) {
      toast.error(error.message)
      throw error
    }
  }

  const refreshUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (user) {
        const profile = await getUserProfile(user.id)
        setUserProfile(profile)
      }
    } catch (error) {
      console.error('Error refreshing user:', error)
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      toast.success("Email enviado! Verifique sua caixa de entrada para redefinir sua senha.")
    } catch (error: any) {
      toast.error(error.message)
      throw error
    }
  }

  useEffect(() => {
    let mounted = true
    let loadingTimeout: NodeJS.Timeout
    let tokenRefreshInterval: NodeJS.Timeout
    let initializationRetries = 0
    const maxRetries = 3
    
    const initializeAuth = async (retryCount = 0) => {
      if (initializationRetries >= maxRetries) {
        console.log('[AUTH] Máximo de tentativas excedido, parando inicialização');
        setLoading(false);
        return;
      }
      
      console.log('[AUTH] Inicializando autenticação...', retryCount > 0 ? `(retry ${retryCount})` : '')
      
      // Timeout de segurança para evitar loading infinito
      loadingTimeout = setTimeout(() => {
        if (mounted) {
          console.log('[AUTH] Timeout de inicialização - forçando loading false')
          setLoading(false)
        }
      }, 5000) // 5 segundos máximo
      
      // Setup token refresh interval to prevent session timeouts
      tokenRefreshInterval = setInterval(async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (session) {
            // Refresh token if it will expire in the next 5 minutes
            const expiresAt = new Date(session.expires_at! * 1000)
            const now = new Date()
            const timeUntilExpiry = expiresAt.getTime() - now.getTime()
            
            if (timeUntilExpiry < 5 * 60 * 1000) { // 5 minutes
              console.log('[AUTH] Refreshing token preventively')
              await supabase.auth.refreshSession()
            }
          }
        } catch (error) {
          console.error('[AUTH] Error in token refresh:', error)
        }
      }, 4 * 60 * 1000) // Check every 4 minutes
      
      // Configurar listener de mudanças de autenticação com proteção adicional
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (!mounted) return
          
          console.log('[AUTH] State changed:', event, session?.user?.id)
          
          try {
            if (session?.user) {
              setUser(session.user)
              // Defer fetching profile to avoid blocking the auth event
              setTimeout(() => {
                if (!mounted) return
                getUserProfile(session.user!.id)
                  .then((profile) => {
                    if (!mounted) return
                    console.log('[AUTH] Profile loaded in listener:', profile?.role);
                    setUserProfile(profile)
                  })
                  .catch((error) => {
                    console.error('[AUTH] Erro ao carregar perfil no listener:', error)
                    if (!mounted) return
                    // Fallback mais robusto
                    const fallbackProfile = {
                      id: session.user!.id,
                      email: session.user!.email || '',
                      full_name: session.user!.user_metadata?.full_name || '',
                      role: 'user' as const,
                      is_active: true,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                      phone: null
                    }
                    console.log('[AUTH] Using fallback profile:', fallbackProfile);
                    setUserProfile(fallbackProfile)
                  })
              }, 100) // Pequeno delay para permitir que o DOM se estabilize
            } else {
              console.log('[AUTH] User signed out');
              setUser(null)
              setUserProfile(null)
              setHasRedirected(false)
            }
          } catch (error) {
            console.error('[AUTH] Erro no listener de auth state:', error);
          }
          
          clearTimeout(loadingTimeout)
          setLoading(false)
        }
      )

      // Check current session
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        console.log('[AUTH] Sessão atual:', session?.user?.id)
        
        if (!mounted) return subscription
        
        if (error && error.message.includes('refresh_token_not_found')) {
          console.log('[AUTH] Token expirado, fazendo refresh...')
          await supabase.auth.refreshSession()
        }
        
        if (session?.user) {
          setUser(session.user)
          const profile = await getUserProfile(session.user.id)
          if (mounted) {
            setUserProfile(profile)
          }
        }
      } catch (error) {
        console.error('[AUTH] Erro ao obter sessão inicial:', error)
        if (mounted) {
          await supabase.auth.signOut()
        }
      }
      
      if (mounted) {
        clearTimeout(loadingTimeout)
        setLoading(false)
      }
      
      return subscription
    }

    let subscription: any
    initializeAuth().then(sub => {
      subscription = sub
    }).catch(error => {
      console.error('[AUTH] Erro na inicialização:', error)
      if (mounted && initializationRetries < maxRetries) {
        initializationRetries++
        console.log(`[AUTH] Tentando novamente inicialização (${initializationRetries}/${maxRetries})`);
        setTimeout(() => {
          initializeAuth(initializationRetries).catch(() => {
            if (mounted) {
              clearTimeout(loadingTimeout)
              setLoading(false)
            }
          });
        }, 2000);
      } else if (mounted) {
        clearTimeout(loadingTimeout)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      clearTimeout(loadingTimeout)
      clearInterval(tokenRefreshInterval)
      if (subscription) {
        subscription.unsubscribe()
      }
    }
  }, [])

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshUser,
    resetPassword,
    redirectAfterLogin,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
